import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { URL_USERS } from '../service/endpoints';
import { deleteData, getData, patchData, postData } from '../service/APIClient';
import store from '../store';
import { getLocalStorage } from '../Helper/helperFunctions';

export const fetchUsers = createAsyncThunk('users/fetchUsers', async () => {
  let users = [];
  let currentPage = 1;
  let totalPages = 1;

  try {
    while (currentPage <= totalPages) {
      const response = await getData(URL_USERS + `?page=${currentPage}`);
      users = [...users, ...response.data];
      totalPages = response.totalPages;
      currentPage++;
    }
    return users.map(user => ({
      key: user._id.toString(),
      username: user.username,
      email: user.email,
      role: user.role,
      active: user.active,
      createdAt: new Date(user.createdAt).toISOString().split('T')[0],
      updatedAt: new Date(user.updatedAt).toISOString().split('T')[0]
    }));
  } catch (error) {
    throw new Error('Failed to fetch users');
  }
});

export const addUserAsync = createAsyncThunk(
  'users/addUser',
  async (newUser, thunkAPI) => {
    // const user = store.getState().auth.user;
    // console.log(user)
    newUser.customer = getLocalStorage("tenantId");
    try {
      return await postData(URL_USERS, newUser);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Update user
export const updateUserAsync = createAsyncThunk(
  'users/updateUser',
  async (updatedUser, thunkAPI) => {
    const { key, ...rest } = updatedUser;
    try {
      return await patchData(`${URL_USERS}/${key}`, rest); // Assumes the response contains the updated user object
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Delete user
export const deleteUserAsync = createAsyncThunk(
  'users/deleteUser',
  async (userId, thunkAPI) => {
    try {
      await deleteData(`${URL_USERS}/${userId}`);
      return userId;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Slice
const usersSlice = createSlice({
  name: 'users',
  initialState: {
    data: [],
    loading: false,
    addStatus: 'idle',
    updateStatus: 'idle',
    error: null
  },
  reducers: {},
  extraReducers: builder => {
    builder
      // Fetch users
      .addCase(fetchUsers.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Add user
      .addCase(addUserAsync.pending, state => {
        state.addStatus = 'loading';
      })
      .addCase(addUserAsync.fulfilled, (state, action) => {
        state.addStatus = 'succeeded';
        state.data.push(action.payload); // Add the new user to the state
      })
      .addCase(addUserAsync.rejected, (state, action) => {
        state.addStatus = 'failed';
        state.error = action.payload || action.error.message;
      })
      // Update user
      .addCase(updateUserAsync.pending, state => {
        state.updateStatus = 'loading';
      })
      .addCase(updateUserAsync.fulfilled, (state, action) => {
        state.updateStatus = 'succeeded';
        const index = state.data.findIndex(
          user => user.key === action.payload._id
        );
        if (index !== -1) {
          state.data[index] = { ...state.data[index], ...action.payload };
        }
      })
      .addCase(updateUserAsync.rejected, (state, action) => {
        state.updateStatus = 'failed';
        state.error = action.payload || action.error.message;
      })
      // Delete user
      .addCase(deleteUserAsync.pending, state => {
        state.deleteStatus = 'loading';
      })
      .addCase(deleteUserAsync.fulfilled, (state, action) => {
        state.deleteStatus = 'succeeded';
        state.data = state.data.filter(user => user.key !== action.payload);
      })
      .addCase(deleteUserAsync.rejected, (state, action) => {
        state.deleteStatus = 'failed';
        state.error = action.payload || action.error.message;
      });
  }
});

// Export actions
export default usersSlice.reducer;
